import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { useHomeworks, useCompleteExercise, useHomeworkDocument } from '@/services/homeworks';
import { BottomTabBar } from '@/components/ui/BottomTabBar';
import { GlobalHeader } from '@/components/GlobalHeader';
import { Spinner } from '@/components/ui/Spinner';

export function HomeworkDetailPage() {
  const { t } = useTranslation();
  const { id: homeworkId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { childId } = useAuthStore();
  const { data, isLoading } = useHomeworks(childId ?? '');
  const { mutate: completeExercise, isPending } = useCompleteExercise(childId ?? '');

  const [showDoc, setShowDoc] = useState(false);
  const { data: docData, isLoading: docLoading } = useHomeworkDocument(
    childId ?? '',
    homeworkId ?? '',
    showDoc,
  );

  // For PDFs: open in new tab when presigned URL arrives
  useEffect(() => {
    if (docData?.media_type === 'application/pdf') {
      window.open(docData.document_url, '_blank');
      setShowDoc(false);
    }
  }, [docData]);

  const homework = data?.homeworks.find((h) => h.homework_id === homeworkId);

  if (isLoading) {
    return (
      <div className="page-center">
        <div className="phone-mockup">
          <GlobalHeader showBack backRoute="/student/homework" />
          <div className="app-content" style={{ display: 'flex', justifyContent: 'center', paddingTop: '48px' }}>
            <Spinner size="lg" />
          </div>
          <BottomTabBar activeTab="Home" />
        </div>
      </div>
    );
  }

  if (!homework) {
    return (
      <div className="page-center">
        <div className="phone-mockup">
          <GlobalHeader showBack backRoute="/student/homework" />
          <div className="app-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '48px', gap: '16px' }}>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>{t('homework.not_found')}</p>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate('/student/homework')}
              style={{ width: 'auto', padding: '8px 20px' }}
            >
              {t('homework.back_to_list')}
            </button>
          </div>
          <BottomTabBar activeTab="Home" />
        </div>
      </div>
    );
  }

  const doneCount = homework.exercises.filter((e) => e.status === 'done').length;
  const total = homework.exercises.length;
  const pct = total > 0 ? Math.round((doneCount / total) * 100) : 0;

  return (
    <div className="page-center">
      <div className="phone-mockup">
        <GlobalHeader showBack backRoute="/student/homework" />
        <div className="app-content" style={{ paddingBottom: '24px' }}>

          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-dark)', margin: '0 0 16px' }}>
            {homework.subject}
          </h1>

          {/* Theme pills */}
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '16px' }}>
            {homework.themes.map((th) => (
              <span
                key={th}
                style={{
                  fontSize: '0.75rem', padding: '3px 10px', borderRadius: '999px',
                  background: 'var(--primary-light)', color: 'var(--primary)', fontWeight: 500,
                }}
              >
                {th}
              </span>
            ))}
          </div>

          {/* View original document (only when attachment exists) */}
          {homework.has_document && (
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setShowDoc(true)}
              disabled={docLoading}
              style={{ width: '100%', marginBottom: '16px', fontSize: '0.875rem' }}
            >
              {docLoading ? t('actions.loading') : t('homework.view_document')}
            </button>
          )}

          {/* Progress bar */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 500 }}>{t('homework.progress_label')}</span>
              <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 600 }}>{doneCount}/{total} {t('homework.done')}</span>
            </div>
            <div style={{ width: '100%', background: '#E5E7EB', borderRadius: '999px', height: '8px' }}>
              <div
                style={{
                  width: `${pct}%`, background: 'var(--primary)',
                  height: '8px', borderRadius: '999px', transition: 'width 0.3s',
                }}
              />
            </div>
          </div>

          {/* Exercise cards */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {homework.exercises.map((ex, i) => (
              <div
                key={ex.exercise_id}
                className="card"
                style={{ padding: '16px' }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: '0 0 4px', fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                      {t('homework.exercise')} {i + 1}
                    </p>
                    <p style={{
                      margin: 0, fontSize: '0.875rem',
                      color: ex.status === 'done' ? 'var(--text-muted)' : 'var(--text-dark)',
                      textDecoration: ex.status === 'done' ? 'line-through' : 'none',
                    }}>
                      {ex.prompt}
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    style={{ marginTop: '2px', width: '18px', height: '18px', accentColor: 'var(--primary)', flexShrink: 0, cursor: 'pointer' }}
                    checked={ex.status === 'done'}
                    disabled={isPending}
                    onChange={() =>
                      completeExercise({
                        homeworkId: homework.homework_id,
                        exerciseId: ex.exercise_id,
                        status: ex.status === 'done' ? 'pending' : 'done',
                      })
                    }
                  />
                </div>
                {ex.status !== 'done' && (
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() =>
                      navigate(`/student/homework/${homework.homework_id}/exercise/${ex.exercise_id}`)
                    }
                    style={{ marginTop: '12px', width: '100%', fontSize: '0.85rem', padding: '8px' }}
                  >
                    💡 {t('homework.ai_help')}
                  </button>
                )}
              </div>
            ))}
          </div>

        </div>
        <BottomTabBar activeTab="Home" />
      </div>

      {/* Full-screen image overlay (images only; PDFs open in new tab) */}
      {showDoc && docData && docData.media_type.startsWith('image/') && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setShowDoc(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: '20px',
          }}
        >
          <img
            src={docData.document_url}
            alt={t('homework.document_alt')}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '100%', maxHeight: '90vh', borderRadius: '8px', objectFit: 'contain' }}
          />
          <button
            type="button"
            onClick={() => setShowDoc(false)}
            style={{
              position: 'absolute', top: '16px', right: '16px',
              background: 'white', border: 'none', borderRadius: '50%',
              width: '36px', height: '36px', fontSize: '1.3rem', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}
